/**
 * Store Zustand pour le parcours répondant.
 *
 * Single source of truth :
 * - `definition` : SurveyDefinition chargée par RespondentApp.
 * - `answers` : map name → value.
 * - `engineState` : recalculé à chaque mutation via `evaluateSurvey`.
 *
 * Le store NE PERSISTE PAS automatiquement (autosave géré séparément par
 * `<Autosave/>`) — on évite les corruptions et on garde le contrôle des
 * I/O.
 */
import { create } from 'zustand';
import {
  evaluateSurvey,
  findItem,
  type Answers,
  type Answer,
  type EngineState,
  type Identity,
  type SurveyDefinition,
} from '@webforge-survey/shared';

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface RespondentState {
  definition: SurveyDefinition | null;
  sessionId: string | null;
  /** Token opaque (hex 64) servant à authentifier la session côté API.
   *  Stocké en localStorage pour la reprise — JAMAIS lié à une PII. */
  resumeToken: string | null;
  answers: Answers;
  identity: Identity;
  /** Tickbox d'opt-in identité (mode optional). Faux par défaut, jamais pré-coché. */
  identityOptIn: boolean;
  /** Index de bloc/écran courant. */
  cursor: number;
  /** Statut autosave pour l'affichage utilisateur. */
  autosaveStatus: AutosaveStatus;
  /** Timestamp ISO de la dernière sauvegarde réussie. */
  lastSavedAt: string | null;

  // Setters ------------------------------------------------------
  setDefinition: (def: SurveyDefinition) => void;
  setSessionId: (id: string) => void;
  setResumeToken: (token: string) => void;
  hydrate: (answers: Answers, identity?: Identity) => void;
  setAnswer: (name: string, value: Answer) => void;
  setIdentity: (identity: Identity) => void;
  setIdentityOptIn: (optIn: boolean) => void;
  setCursor: (n: number) => void;
  setAutosaveStatus: (status: AutosaveStatus, savedAt?: string) => void;
  reset: () => void;

  // Selectors ----------------------------------------------------
  /**
   * Calcule l'état moteur. Pure function appelée par les composants ;
   * Zustand re-render uniquement si les `answers` ou `definition` changent.
   */
  engineState: () => EngineState | null;
}

export const useRespondentStore = create<RespondentState>((set, get) => ({
  definition: null,
  sessionId: null,
  resumeToken: null,
  answers: {},
  identity: null,
  identityOptIn: false,
  cursor: 0,
  autosaveStatus: 'idle',
  lastSavedAt: null,

  setDefinition: (def) => set({ definition: def }),
  setSessionId: (id) => set({ sessionId: id }),
  setResumeToken: (token) => set({ resumeToken: token }),

  hydrate: (answers, identity) =>
    set({
      answers,
      identity: identity ?? null,
      identityOptIn: Boolean(identity && Object.keys(identity).length > 0),
    }),

  setAnswer: (name, value) => {
    const def = get().definition;
    const prev = get().answers;
    const next: Answers = { ...prev, [name]: value };

    /*
     * Gestion option exclusive : si on coche une valeur listée dans
     * `exclusive` d'un select_multiple, on vide les autres ; inversement, si
     * on coche une non-exclusive alors qu'une exclusive est déjà cochée, on
     * retire l'exclusive. Comportement immédiat (UX réassurante) avant que
     * le moteur ne le signale en erreur.
     */
    if (def && Array.isArray(value)) {
      const item = findItem(def, name);
      if (item && item.type === 'select_multiple' && item.exclusive && item.exclusive.length > 0) {
        const exclusives = item.exclusive;
        const prevArr = Array.isArray(prev[name]) ? (prev[name] as string[]) : [];
        const newArr = value;

        const newlyAddedExclusive = newArr.find(
          (v) => exclusives.includes(v) && !prevArr.includes(v),
        );
        const newlyAddedRegular = newArr.find(
          (v) => !exclusives.includes(v) && !prevArr.includes(v),
        );

        if (newlyAddedExclusive) {
          // Désélectionne tout sauf cette valeur exclusive
          next[name] = [newlyAddedExclusive];
        } else if (newlyAddedRegular) {
          // Retire les exclusives déjà présentes
          next[name] = newArr.filter((v) => !exclusives.includes(v));
        }
      }
    }

    set({ answers: next, autosaveStatus: 'idle' });
  },

  setIdentity: (identity) => set({ identity }),
  setIdentityOptIn: (optIn) =>
    set((s) => ({
      identityOptIn: optIn,
      // Si on décoche, on RETIRE l'identité (anti-rebinding).
      identity: optIn ? s.identity : null,
    })),
  setCursor: (n) => set({ cursor: Math.max(0, n) }),
  setAutosaveStatus: (status, savedAt) =>
    set((s) => ({
      autosaveStatus: status,
      lastSavedAt: status === 'saved' && savedAt ? savedAt : s.lastSavedAt,
    })),
  reset: () =>
    set({
      definition: null,
      sessionId: null,
      resumeToken: null,
      answers: {},
      identity: null,
      identityOptIn: false,
      cursor: 0,
      autosaveStatus: 'idle',
      lastSavedAt: null,
    }),

  engineState: () => {
    const def = get().definition;
    const ans = get().answers;
    if (!def) return null;
    return evaluateSurvey(def, ans);
  },
}));
